/**
 * Estilos da página do setor. Só tokens `var(--*)` (claro e escuro saem do
 * mesmo CSS) e cor só para estado: info = em produção/andamento, warning =
 * pausado, danger = atrasado, success = concluído, neutral = o resto. O
 * accent da marca fica para seleção, foco e a ação principal.
 */
import { motion } from "framer-motion";
import { mq } from "../../../styles/canonical/breakpoints";
import styled, { css, keyframes } from "styled-components";

const TONS = {
  info: css`
    background: var(--info-bg);
    border-color: var(--info-border);
    color: var(--info-text);
    --tom-solido: var(--info-solid);
  `,
  warning: css`
    background: var(--warning-bg);
    border-color: var(--warning-border);
    color: var(--warning-text);
    --tom-solido: var(--warning-solid);
  `,
  danger: css`
    background: var(--danger-bg);
    border-color: var(--danger-border);
    color: var(--danger-text);
    --tom-solido: var(--danger-solid);
  `,
  success: css`
    background: var(--success-bg);
    border-color: var(--success-border);
    color: var(--success-text);
    --tom-solido: var(--success-solid);
  `,
  neutral: css`
    background: var(--neutral-bg);
    border-color: var(--neutral-border);
    color: var(--neutral-text);
    --tom-solido: var(--neutral-solid);
  `,
};

export const tom = (nome) => TONS[nome] || TONS.neutral;

const SOLIDO = {
  info: "var(--info-solid)",
  warning: "var(--warning-solid)",
  danger: "var(--danger-solid)",
  success: "var(--success-solid)",
  neutral: "var(--border-strong)",
};

const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";

const focoVisivel = css`
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

/**
 * No tablet (dedo) todo alvo cresce para 44 px; no mouse a tela segue densa.
 */
const alvoNoToque = css`
  @media (pointer: coarse) {
    min-height: 44px;
  }
`;

// ------------------------------------------------------------------ página

export const Pagina = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  min-width: 0;
  padding: 16px 24px 48px;

  @media ${mq.mobile} {
    padding: 12px 16px 32px;
  }
`;

export const Zona1 = styled.div`
  display: flex;
  flex-direction: column;

  .eh {
    padding: 14px 18px;
    flex-wrap: wrap;
    row-gap: 10px;
  }
  /* "Mapa" (voltar) e os botões do cabeçalho vêm do design system com 29 px
     de altura: no dedo viram alvo de 44. */
  @media (pointer: coarse) {
    .eh__back,
    .eh .btn {
      min-height: 44px;
    }
  }
  .kpi-strip-v2 {
    margin-bottom: 0 !important;
  }
  .kpi-strip-v2 .kpi-v2 {
    padding: 12px 18px;
  }
  .kpi-strip-v2 .kpi-v2[role="button"]:hover {
    background: var(--surface-hover);
  }
  .kpi-strip-v2 .kpi-v2[role="button"]:focus-visible {
    outline: none;
    box-shadow: inset var(--shadow-focus);
  }
`;

export const BotoesCabecalho = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// ------------------------------------------------------------ equipe (jogo)

export const FaixaEquipe = styled.section`
  display: flex;
  align-items: center;
  gap: 8px 10px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);

  > .rotulo {
    font-family: var(--font-mono);
    font-size: 11px;
    @media ${mq.tablet} { font-size: var(--text-min, 12px); }
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-3);
    margin-right: 4px;
  }
  > .vazio {
    font-size: 13px;
    color: var(--ink-3);
  }
`;

export const Pessoa = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  /* Cresce para preencher a linha: a faixa fica em blocos iguais, sem o
     serrilhado de pílulas de tamanhos diferentes. */
  flex: 1 1 220px;
  min-width: 0;
  min-height: 44px;
  max-width: 360px;
  padding: 4px 12px 4px 4px;
  border: 1px solid ${({ $ativa }) => ($ativa ? "var(--accent)" : "var(--border)")};
  border-radius: 999px;
  background: ${({ $ativa }) => ($ativa ? "var(--accent-soft)" : "var(--surface)")};
  color: var(--ink);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ${EASE_OUT}, transform 160ms ${EASE_OUT};
  ${focoVisivel}

  &:hover {
    border-color: var(--border-strong);
  }
  &:active {
    transform: scale(0.97);
  }
  > .texto {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.25;
  }
  .nome {
    font-size: 13px;
    font-weight: 600;
  }
  .fazendo {
    font-size: 12px;
    color: var(--ink-3);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &[data-estado="trabalhando"] .fazendo {
    color: var(--info-text);
  }
  &[data-estado="pausado"] .fazendo {
    color: var(--warning-text);
  }
`;

const pulso = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  70% { transform: scale(1.35); opacity: 0; }
  100% { transform: scale(1.35); opacity: 0; }
`;

/** Avatar de iniciais: neutro; o anel diz o que a pessoa está fazendo. */
export const Avatar = styled.span`
  position: relative;
  flex: 0 0 auto;
  width: ${({ $tamanho }) => $tamanho || 32}px;
  height: ${({ $tamanho }) => $tamanho || 32}px;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  background: var(--surface-2);
  color: var(--ink-2);
  font-size: ${({ $tamanho }) => Math.max(9, Math.round((($tamanho || 32) * 0.36)))}px;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 0 0 2px var(--surface), 0 0 0 ${({ $tamanho }) => (($tamanho || 32) >= 28 ? 4 : 3)}px
    ${({ $tom }) => SOLIDO[$tom] || SOLIDO.neutral};

  &[data-trabalhando="sim"]::after {
    content: "";
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    border: 2px solid var(--info-solid);
    animation: ${pulso} 2.4s ${EASE_OUT} infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    &[data-trabalhando="sim"]::after {
      animation: none;
      opacity: 0;
    }
  }
`;

export const Avatares = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

// ------------------------------------------------------------------ barra

export const Barra = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

export const Espaco = styled.span`
  flex: 1 1 auto;
`;

export const Segmentado = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 10px;
  background: var(--surface-2);
  border: 1px solid var(--border);
`;

export const Segmento = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  ${alvoNoToque}
  padding: 0 12px;
  border: none;
  border-radius: 7px;
  background: ${({ $ativo }) => ($ativo ? "var(--surface)" : "transparent")};
  box-shadow: ${({ $ativo }) => ($ativo ? "var(--shadow-1)" : "none")};
  /* ink-2 no inativo: com ink-3 o contraste ficava abaixo de 4,5:1 em 13 px. */
  color: ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--ink-2)")};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: color 150ms ${EASE_OUT}, background 150ms ${EASE_OUT};
  ${focoVisivel}

  &:hover {
    color: var(--ink);
  }
  .n {
    font-family: var(--font-mono);
    font-size: 11px;
    @media ${mq.tablet} { font-size: var(--text-min, 12px); }
    font-weight: 500;
    color: var(--ink-3);
  }
  @media (max-width: 1100px) {
    &[data-compacto="sim"] .texto {
      display: none;
    }
  }
`;

export const CampoBusca = styled.label`
  position: relative;
  flex: 1 1 240px;
  min-width: 180px;
  max-width: 420px;
  display: flex;
  align-items: center;

  > svg {
    position: absolute;
    left: 12px;
    color: var(--ink-3);
    pointer-events: none;
  }
  input {
    width: 100%;
    min-width: 0;
    height: 40px;
    @media (pointer: coarse) {
      height: 44px;
    }
    padding: 0 12px 0 34px;
    border-radius: 9px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: 13px;
  }
  input::placeholder {
    color: var(--ink-3);
  }
  input:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: var(--shadow-focus);
  }
`;

export const BotaoBarra = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  ${alvoNoToque}
  padding: 0 14px;
  border-radius: 9px;
  border: 1px solid ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--border)")};
  background: ${({ $ativo }) => ($ativo ? "var(--accent-soft)" : "var(--surface)")};
  /* Ativo = borda + fundo do accent, texto em ink: laranja sobre laranja
     claro não chega a 4,5:1 em 13 px. */
  color: ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--ink-2)")};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  ${focoVisivel}

  &:hover {
    border-color: var(--border-strong);
    color: var(--ink);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Contador = styled.span`
  display: inline-grid;
  place-items: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--accent);
  color: var(--ink-on-accent);
  font-family: var(--font-mono);
  font-size: 11px;
  @media ${mq.tablet} { font-size: var(--text-min, 12px); }
  font-weight: 600;
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

export const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  @media (pointer: coarse) {
    min-height: 40px;
  }
  max-width: 100%;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--border)")};
  background: ${({ $ativo }) => ($ativo ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--ink-2)")};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  ${focoVisivel}

  > .texto {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > .rotulo {
    color: var(--ink-3);
    font-weight: 500;
  }
  > .n {
    font-family: var(--font-mono);
    font-size: 11px;
    @media ${mq.tablet} { font-size: var(--text-min, 12px); }
    color: var(--ink-3);
    font-weight: 500;
  }
  > .ponto {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $tom }) => SOLIDO[$tom] || SOLIDO.neutral};
  }
`;

export const AoVivo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--ink-3);
  white-space: nowrap;

  > .ponto {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $erro }) => ($erro ? "var(--warning-solid)" : "var(--success-solid)")};
  }
`;

export const Aviso = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 10px 14px;
  border: 1px solid;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  ${({ $tom }) => tom($tom || "danger")}

  /* Convite (setor ainda por montar) não é aviso: traço interrompido e peso
     de texto normal, o mesmo desenho do lugar vago no mapa. Em negrito e com
     borda cheia, "ainda não tem subsetores" soava como erro. */
  &[data-convite="sim"] {
    border-style: dashed;
    font-weight: 500;
  }
`;

// ------------------------------------------------------------------ kanban

export const Quadro = styled.div`
  display: grid;
  gap: 12px;
  align-items: start;
  grid-template-columns: repeat(
    auto-fit,
    minmax(max(272px, calc((100% - ${({ $colunas }) => ($colunas - 1) * 12}px) / ${({ $colunas }) => $colunas})), 1fr)
  );
`;

export const Coluna = styled.section`
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-height: max(520px, calc(100vh - 300px));
  max-height: max(520px, calc(100dvh - 300px));
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface-2);
  overflow: hidden;

  &[data-inativo="sim"] {
    border-style: dashed;
  }
`;

export const CabecaColuna = styled.header`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 8px 10px 8px 14px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);

  > .titulo {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 14px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > .n {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--ink-3);
  }
`;

export const BotaoIcone = styled.button`
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  @media (pointer: coarse) {
    width: 44px;
    height: 44px;
  }
  display: inline-grid;
  place-items: center;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-2);
  cursor: pointer;
  ${focoVisivel}

  &:hover:not(:disabled) {
    border-color: var(--border-strong);
    color: var(--ink);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const CorpoColuna = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
`;

export const Grupo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* Não encolher: sem isso a coluna com altura máxima espremia cada grupo
     até 56 px e os cartões se sobrepunham. */
  flex: none;
  min-height: 56px;
  padding: 8px;
  border-radius: 10px;
  border: 1px dashed transparent;
  transition: border-color 150ms ${EASE_OUT}, background 150ms ${EASE_OUT};

  &[data-posto="sim"] {
    border: 1px solid var(--border);
  }
  &[data-arrastando="sim"] {
    border-color: var(--border-strong);
    border-style: dashed;
  }
  &[data-sobre="sim"] {
    border-color: var(--accent);
    border-style: dashed;
    background: var(--accent-soft);
  }
  > .vazio {
    font-size: 12px;
    color: var(--ink-3);
    text-align: center;
    padding: 6px 0;
  }
`;

// Coluna larga (poucos subsetores) usa a largura: cartões lado a lado.
export const ListaCartoes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(248px, 1fr));
  gap: 8px;
  min-width: 0;
`;

export const CabecaGrupo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  > .titulo {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--ink-2);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > .n {
    font-family: var(--font-mono);
    font-size: 11px;
    @media ${mq.tablet} { font-size: var(--text-min, 12px); }
    color: var(--ink-2);
  }
`;

// Anel que acende e apaga quando o cartão muda (outro gestor apontou algo):
// só opacidade, no pseudo-elemento, para não repintar o cartão.
const destaque = keyframes`
  0% { opacity: 0; }
  20% { opacity: 1; }
  100% { opacity: 0; }
`;

// motion.div: o cartão desliza até o novo lugar quando o estado muda.
export const Cartao = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: var(--shadow-1);
  color: var(--ink);
  cursor: pointer;
  text-align: left;
  transition: border-color 150ms ${EASE_OUT};
  ${focoVisivel}

  &:hover {
    border-color: var(--border-strong);
  }
  &[data-arrastavel="sim"] {
    touch-action: pan-y;
    user-select: none;
    -webkit-user-select: none;
  }
  &[data-atrasado="sim"] {
    border-color: var(--danger-border);
  }
  &[data-origem="sim"] {
    opacity: 0.35;
  }
  &[data-encerrado="sim"] {
    opacity: 0.6;
  }
  &[data-mudou="sim"]::after {
    content: "";
    position: absolute;
    inset: -3px;
    border: 2px solid var(--info-border);
    border-radius: 12px;
    pointer-events: none;
    opacity: 0;
    animation: ${destaque} 1.6s ${EASE_OUT};
  }
  @media (prefers-reduced-motion: reduce) {
    &[data-mudou="sim"]::after {
      animation: none;
      opacity: 1;
    }
  }

  .linha1 {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .codigo {
    font-family: var(--font-mono);
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-2);
    white-space: nowrap;
  }
  .titulo {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sub {
    font-size: 12px;
    color: var(--ink-3);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 10px;
    font-size: 12px;
    color: var(--ink-2);
  }
  .meta .danger {
    color: var(--danger-text);
    font-weight: 600;
  }
  .meta .warning {
    color: var(--warning-text);
    font-weight: 600;
  }
  .pessoas {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-size: 12px;
    color: var(--ink-2);
  }
  .pessoas .nomes {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Fantasma = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1300;
  pointer-events: none;
  opacity: 0.94;
  filter: drop-shadow(0 12px 24px rgba(11, 15, 26, 0.24));
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  height: 22px;
  padding: 0 8px;
  border: 1px solid;
  border-radius: 999px;
  font-size: 11px;
  @media ${mq.tablet} { font-size: var(--text-min, 12px); }
  font-weight: 600;
  white-space: nowrap;
  ${({ $tom }) => tom($tom)}

  &[data-ponto="sim"]::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--tom-solido);
  }
`;

export const Selos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

// ---------------------------------------------------------------- painéis

export const Secao = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 0;
  border-top: 1px solid var(--border-subtle);

  &:first-child {
    border-top: 0;
    padding-top: 0;
  }
  > h3 {
    margin: 0;
    font-size: 12px;
    font-family: var(--font-mono);
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-3);
  }
  > .nota {
    margin: 0;
    font-size: 12px;
    color: var(--ink-3);
  }
`;

export const Dados = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px 16px;

  > div {
    min-width: 0;
  }
  dt {
    font-size: 11px;
    @media ${mq.tablet} { font-size: var(--text-min, 12px); }
    color: var(--ink-3);
  }
  dd {
    margin: 2px 0 0;
    font-size: 13px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

export const Campos = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${({ $min }) => $min || 180}px, 1fr));
  gap: 12px;
`;

export const Campo = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);

  input,
  select,
  textarea {
    width: 100%;
    min-width: 0;
    min-height: 40px;
    ${alvoNoToque}
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--ink);
    font: inherit;
    font-size: 13px;
    font-weight: 400;
  }
  textarea {
    min-height: 72px;
    resize: vertical;
  }
  input:focus,
  select:focus,
  textarea:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: var(--shadow-focus);
  }
  input:disabled,
  select:disabled,
  textarea:disabled {
    background: var(--surface-2);
    color: var(--ink-3);
  }
  > .ajuda {
    font-weight: 400;
    color: var(--ink-3);
  }
`;

/** Texto de ajuda de um campo, fora do rótulo (ligado por aria-describedby). */
export const Ajuda = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: var(--ink-3);
`;

export const Acoes = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  &[data-fim="sim"] {
    justify-content: flex-end;
  }
`;

export const ErroInline = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid;
  border-radius: 8px;
  font-size: 13px;
  ${tom("danger")}
`;

export const Lista = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ItemLista = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--surface);

  &[data-clicavel="sim"] {
    cursor: pointer;
  }
  &[data-clicavel="sim"]:hover {
    border-color: var(--border-strong);
  }
  &[data-inativo="sim"] {
    opacity: 0.6;
  }
  > .principal {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .l1 {
    font-size: 13px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .l2 {
    font-size: 12px;
    color: var(--ink-3);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Vazio = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--ink-3);
  border: 1px dashed var(--border);
  border-radius: 12px;
  background: var(--surface);
`;

export const Marcas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Marca = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  ${alvoNoToque}
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${({ $ativo }) => ($ativo ? "var(--accent)" : "var(--border)")};
  background: ${({ $ativo }) => ($ativo ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $ativo }) => ($ativo ? "var(--ink)" : "var(--ink-2)")};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  /* Âncora da caixinha escondida: sem isto ela herda o width: 100% global
     dos inputs a partir do painel inteiro e abre rolagem lateral. */
  position: relative;

  input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    pointer-events: none;
  }
  &:focus-within {
    box-shadow: var(--shadow-focus);
  }
`;

// --------------------------------------------------------------- timeline

export const CartaoTimeline = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-1);
  overflow: hidden;
`;

export const CabecaTimeline = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border);

  > .info {
    margin-left: auto;
    font-size: 12px;
    color: var(--ink-3);
    white-space: nowrap;
  }
`;

export const Dia = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  border-bottom: 1px solid var(--border-subtle);

  &:last-child {
    border-bottom: 0;
  }
  @media ${mq.mobile} {
    grid-template-columns: 1fr;
  }
  &[data-compacto="sim"] {
    grid-template-columns: 1fr;
  }
`;

export const ColunaDia = styled.div`
  padding: 14px 16px;
  border-right: 1px solid var(--border-subtle);
  background: ${({ $hoje }) => ($hoje ? "var(--accent-soft)" : "transparent")};

  > .dds {
    font-size: 11px;
    @media ${mq.tablet} { font-size: var(--text-min, 12px); }
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ $hoje }) => ($hoje ? "var(--ink)" : "var(--ink-3)")};
  }
  > .dia {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
  }
  > .mes {
    font-size: 12px;
    /* No dia de hoje o fundo é tingido (--accent-soft): o --ink-3 cai para
       3.8:1 ali. Acompanha o .dds e escurece, como no resto da coluna. */
    color: ${({ $hoje }) => ($hoje ? "var(--ink-2)" : "var(--ink-3)")};
  }
  @media ${mq.mobile} {
    border-right: 0;
    border-bottom: 1px solid var(--border-subtle);
  }
  [data-compacto="sim"] > & {
    border-right: 0;
    border-bottom: 1px solid var(--border-subtle);
    padding: 8px 12px;
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  [data-compacto="sim"] > & > .dia {
    font-size: 15px;
  }
`;

export const Evento = styled.button`
  display: grid;
  grid-template-columns: 52px 28px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 9px 16px;
  border: none;
  border-bottom: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--ink);
  text-align: left;
  font: inherit;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};

  &:last-child {
    border-bottom: 0;
  }
  &:not(:disabled):hover {
    background: var(--surface-hover);
  }
  ${focoVisivel}

  > .hora {
    font-family: var(--font-mono);
    font-size: 11.5px;
    @media ${mq.tablet} { font-size: var(--text-min, 12px); }
    color: var(--ink-3);
  }
  > .texto {
    min-width: 0;
  }
  > .texto .l1 {
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  > .texto .l2 {
    display: block;
    font-size: 12px;
    color: var(--ink-2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  @media (max-width: 560px) {
    grid-template-columns: 44px 28px minmax(0, 1fr);
    > .tag {
      display: none;
    }
  }
`;

export const IconeEvento = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-grid;
  place-items: center;
  border: 1px solid;
  ${({ $tom }) => tom($tom)}
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 6px;
  background: var(--surface-2);
  color: var(--ink-3);
  font-size: 11px;
  @media ${mq.tablet} { font-size: var(--text-min, 12px); }
  font-weight: 600;
  white-space: nowrap;
`;

// ------------------------------------------------------------ visão Mapa

/**
 * Quadro da visão Mapa: a planta do setor à esquerda e o painel do que está
 * selecionado à direita. Abaixo de 1100 px o painel desce para baixo da
 * planta (o tablet em retrato é o caso normal na fábrica) — nunca vira
 * scroll lateral.
 */
export const QuadroMapa = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 12px;
  align-items: start;
  min-width: 0;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const BarraMapa = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  min-width: 0;

  /* "Descartar" e "Salvar desenho" são as ações do editor de planta: no dedo
     precisam do mesmo alvo de 44 px do resto da tela. */
  button {
    ${alvoNoToque}
  }
  /* O que ainda não foi salvo. ink-2, e não ink-3: é o aviso que segura o
     gestor antes de ele sair da tela. */
  > .pendencia {
    font-size: 12px;
    color: var(--ink-2);
  }
`;

/**
 * A planta. `touch-action: none` para o dedo desenhar em vez de rolar a
 * página; o cursor conta o modo (cruz para desenhar, mão para organizar).
 */
export const Planta = styled.div`
  position: relative;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--bg);
  overflow: hidden;
  min-width: 0;

  /* O quadro segue a PROPORÇÃO da sala (--proporcao, do viewBox), com piso e
     teto. Altura fixa deixava uma sala larga e baixa desenhada no meio de 400
     px de vazio — e no tablet em pé isso empurrava a legenda para fora da
     tela. */
  > svg {
    display: block;
    width: 100%;
    aspect-ratio: var(--proporcao, 3 / 2);
    height: auto;
    min-height: 320px;
    max-height: min(68vh, 720px);
    touch-action: none;
    user-select: none;
  }
  &[data-modo="organizar"] > svg {
    cursor: default;
  }
  &[data-modo="subsetor"] > svg,
  &[data-modo="posto"] > svg {
    cursor: crosshair;
  }

  /* Formas: a hierarquia é de PESO de superfície, não de cor — a cor fica
     reservada para estado (atraso, fora do pai). */
  .sala {
    fill: var(--surface);
    stroke: var(--border-strong);
    stroke-width: 2;
  }
  .grade {
    stroke: var(--border-subtle);
    stroke-width: 1;
  }
  .forma {
    stroke: var(--border-strong);
    stroke-width: 1.5;
    transition: fill 160ms ${EASE_OUT}, stroke 160ms ${EASE_OUT};
  }
  @media (prefers-reduced-motion: reduce) {
    .forma {
      transition: none;
    }
  }
  .forma[data-alvo="subsetor"] {
    fill: var(--surface-2);
  }
  .forma[data-alvo="posto"] {
    fill: var(--surface-3);
  }
  .forma[data-semdesenho="sim"] {
    stroke-dasharray: 8 6;
  }
  .forma[data-inativo="sim"] {
    opacity: 0.45;
  }
  .forma[data-selecionado="sim"] {
    stroke: var(--accent);
    stroke-width: 2.5;
  }
  .forma[data-fora="sim"] {
    stroke: var(--danger-solid);
    stroke-dasharray: 10 6;
  }
  .forma[data-arrastavel="sim"] {
    cursor: grab;
  }
  .anel {
    fill: none;
    stroke: var(--accent-ring);
    stroke-width: 7;
  }
  .rotulo {
    fill: var(--ink-2);
    font-weight: 700;
    pointer-events: none;
  }
  .rotulo--posto {
    fill: var(--ink-3);
    font-weight: 600;
  }
  .contagem {
    fill: var(--ink-3);
    font-weight: 500;
    pointer-events: none;
  }
  .atraso {
    fill: var(--danger-text);
    font-weight: 700;
    pointer-events: none;
  }
  .alca {
    fill: var(--surface);
    stroke: var(--accent);
    stroke-width: 2;
    cursor: nwse-resize;
  }
  /* Alvo de toque da alça: invisível e do tamanho do dedo (44 px de tela). O
     quadradinho que se vê fica dentro dele, menor, para não cobrir a quina. */
  .alca-alvo {
    fill: transparent;
    stroke: none;
    cursor: nwse-resize;
  }
  /* Traço a mão livre: a espessura vem do componente, em pixels de tela. */
  .tracando {
    fill: var(--accent-soft);
    fill-opacity: 0.5;
    stroke: var(--accent);
    stroke-dasharray: 6 4;
    stroke-linecap: round;
    stroke-linejoin: round;
    pointer-events: none;
  }
`;

/** Aviso do editor (o que foi encaixado, o que não cabe, o que falta salvar). */
export const AvisoMapa = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 0;
  padding: 8px 10px;
  border: 1px solid;
  border-radius: 10px;
  font-size: 12.5px;
  ${({ $tom }) => tom($tom)}
`;

/** Painel lateral do elemento selecionado no mapa. */
export const PainelElemento = styled.aside`
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;

  /* Renomear, desativar, trocar de subsetor: é aqui que se edita o que foi
     desenhado, e no tablet isso é feito com o dedo. */
  button,
  select,
  input {
    ${alvoNoToque}
  }

  h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: var(--ink);
  }
  .vazio {
    margin: 0;
    font-size: 12.5px;
    color: var(--ink-3);
  }
  > div {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }
`;

/** Legenda da planta: o que cada peso de superfície quer dizer. */
export const LegendaMapa = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 8px 0 0;
  padding: 0;
  font-size: 12px;
  color: var(--ink-3);

  > li {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  > li > span[data-amostra] {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 1px solid var(--border-strong);
  }
  > li > span[data-amostra="sala"] {
    background: var(--surface);
  }
  > li > span[data-amostra="subsetor"] {
    background: var(--surface-2);
  }
  > li > span[data-amostra="posto"] {
    background: var(--surface-3);
  }
  > li > span[data-amostra="fora"] {
    background: var(--danger-bg);
    border-color: var(--danger-solid);
    border-style: dashed;
  }
`;
